// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import validateReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ValidateReCaptcha";
import { InvoiceStateContext } from "@insite/client-framework/Store/Data/Invoices/InvoicesSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ReCaptcha from "@insite/content-library/Components/ReCaptcha";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { Checkbox, DataTableBody, DataTableHead, DataTableHeader } from "@insite/mobius/index";
import LoadingOverlay, { LoadingOverlayProps } from "@insite/mobius/LoadingOverlay";
import TextField, { TextFieldProps } from "@insite/mobius/TextField";
import { useToaster } from "@insite/mobius/Toast/ToasterContext";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Block, { BlockProps } from "@store/Library/Components/Toolkit/Block";
import FormWrapper from "@store/Library/Components/Toolkit/FormWrapper";
import { RequiredWrapper } from "@store/MobiusOverrides/FormField/FormField";
import { formGridProps } from "@store/Themes/common/props/form";
import { orderProps } from "@store/Themes/common/props/order";
import constants from "@store/constants";
import displayInvoiceRmaLines from "@store/Framework/Store/Store/Pages/ReturnRequest/Handlers/DisplayInvoiceRmaLines";
import submitReturnRequestForm from "@store/Framework/Store/Store/Pages/ReturnRequest/Handlers/SubmitReturnRequestForm";
import updateInvoiceRmaLine from "@store/Framework/Store/Store/Pages/ReturnRequest/Handlers/UpdateInvoiceRmaLine";
import { useInvoiceRmaLines } from "@store/Framework/Store/Store/Pages/ReturnRequest/ReturnRequestHooks";
import { InvoiceRmaLine, ReturnReasonModel } from "@store/Framework/Types/ApiModels";
import ReturnRequestLineCard from "@store/Library/Components/ReturnRequest/ReturnRequestLineCard";
import useIsBreakpoint from "@toolkit/Framework/Common/Hooks/useIsBreakpoint";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";
import getRequiredFieldMessage from "@toolkit/Framework/Common/Utilities/forms/getRequiredFieldMessage";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { pxToRem, pxToRemArray } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { useSession } from "@toolkit/Framework/Store/Context/ContextHooks";
import React, { ChangeEvent, createRef, FormEvent, RefObject, useContext, useEffect, useMemo, useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    returnSlipPageUrl: getPageLinkByPageType(state, constants.pageType.returnSlipPage)?.url,
});

const mapDispatchToProps = {
    validateReCaptcha: makeHandlerChainAwaitable<
        { location: string; recaptchaRef: RefObject<ReCAPTCHA | null> },
        boolean
    >(validateReCaptcha),
    displayInvoiceRmaLines,
    updateInvoiceRmaLine,
    submitReturnRequestForm,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ReturnRequestFormStyles {
    block?: BlockProps;
    loadingOverlay?: LoadingOverlayProps;
    container?: GridContainerProps;
    practiceContactGridItem?: GridItemProps;
    practiceContactTextField?: TextFieldProps;
    invoiceLinesGridItem?: GridItemProps;
    dataTable?: DataTableProps;
    tableHead?: DataTableHeadProps;
    tableHeader?: DataTableHeaderProps;
    tableBody?: DataTableBodyProps;
    formActionsWrapper?: InjectableCss;
    returnAgreement?: CheckboxPresentationProps;
    returnRequestButton?: ButtonPresentationProps;
}

const getColumnSize = (columns: number) => `${(columns / 12) * 100}%`;

const columnSizes = css`
    &.description-column {
        ${breakpointValues([getColumnSize(12), getColumnSize(6), null, getColumnSize(4), null], "width")}
        padding-left: 0;
        ${breakpointValues(pxToRemArray([10, 30, null, 10, null]), "padding-right")}
    }

    &.quantity-column {
        text-align: center;
        ${breakpointValues([getColumnSize(0), null, null, getColumnSize(1), null], "width")}
    }

    &.fridgeref-column {
        text-align: center;
        ${breakpointValues([getColumnSize(0), null, null, getColumnSize(2), null], "width")}
    }

    &.return-column {
        ${breakpointValues([getColumnSize(0), getColumnSize(6), null, getColumnSize(5), null], "width")}
        padding-left: 0;
        padding-right: 0;
    }
`;

export const returnRequestForm: ReturnRequestFormStyles = {
    block: orderProps.block,
    container: mergeProps<ReturnRequestFormStyles["container"]>(formGridProps.gridContainer, {
        css: css`
            > ${GridOffset} {
                flex-direction: column;
            }
        `,
    }),
    practiceContactGridItem: { width: [12, 6, 6, 4, 4] },
    invoiceLinesGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    formActionsWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: ${pxToRem(15)};
            padding-top: ${pxToRem(30)};
        `,
    },
    dataTable: {
        cssOverrides: {
            table: css`
                border: none;
            `,
            header: css`
                padding: 0 ${pxToRem(10)};
                height: ${pxToRem(30)};
                color: ${getColor("primary.main")};
                background-color: ${getColor("common.background")};
                font-size: ${x => x.theme.design.typography.size.base};
                border-bottom: 1px solid ${getColor("primary.main")};

                ${columnSizes}
            `,
            cell: css`
                padding: ${pxToRem(16)} ${pxToRem(10)};
                border: 0;

                ${columnSizes}
            `,
            row: css`
                border-bottom: 1px solid ${getColor("common.border")};
            `,
            evenRow: css`
                background-color: ${getColor("common.background")};
                border-bottom: 1px solid ${getColor("common.border")};
            `,
        },
    },
};

const styles = returnRequestForm;

const ReturnRequestForm = ({
    returnSlipPageUrl,
    validateReCaptcha,
    displayInvoiceRmaLines,
    updateInvoiceRmaLine,
    submitReturnRequestForm,
}: Props) => {
    const [inProgress, setInProgress] = useState(false);
    const [reloadInvoice, setReloadInvoice] = useState(false);
    const [returnAgreement, setReturnAgreement] = useState(false);
    const [practiceContact, setPracticeContact] = useState("");
    const [practiceContactError, setPracticeContactError] = useState("");
    const [generalError, setGeneralError] = useState("");
    const [invoiceRmaLineErrors, setInvoiceRmaLineErrors] = useState<SafeDictionary<string> | null>(null);
    const recaptchaRef: RefObject<ReCAPTCHA | null> = createRef();
    const { value: invoice, isLoading } = useContext(InvoiceStateContext);
    const invoiceRmaLines = useInvoiceRmaLines();
    const session = useSession();
    const toasterContext = useToaster();
    const isDesktop = useIsBreakpoint("lg", "up");
    const isTablet = useIsBreakpoint("sm", "up");
    /* Store Custom Code - Start */
    const wasLoadingRef = useRef(false);
    /* Store Custom Code - End */

    useEffect(() => {
        /* Store Custom Code - Start */
        const justFinishedLoading = wasLoadingRef.current && !isLoading;
        wasLoadingRef.current = isLoading;
        /* Store Custom Code - End */
        if (
            invoice &&
            !isLoading &&
            invoice.invoiceLines &&
            (typeof invoiceRmaLines[invoice.invoiceNumber] === "undefined" || reloadInvoice || justFinishedLoading)
        ) {
            displayInvoiceRmaLines({ invoice });
            setReloadInvoice(false);
        }
    }, [invoice, isLoading, invoiceRmaLines]);

    const selectedInvoiceRmaLines = useMemo(() => {
        if (!invoice || typeof invoiceRmaLines[invoice.invoiceNumber] === "undefined") {
            return [];
        }

        return Object.entries(invoiceRmaLines[invoice.invoiceNumber] ?? {});
    }, [invoice, invoiceRmaLines]);

    const totalQuantity = useMemo(() => {
        let tQty = 0;

        selectedInvoiceRmaLines.forEach(([key, selectedInvoiceRmaLine]) => {
            tQty += (selectedInvoiceRmaLine?.rmaQtyRequested || 0) > 0 ? 1 : 0;
        });

        return tQty;
    }, [selectedInvoiceRmaLines]);

    const generateButtonDisabled = useMemo(() => {
        return (
            invoice !== undefined &&
            selectedInvoiceRmaLines.every(
                ([key, rmaLine]) => rmaLine && rmaLine.nonReturnableMessage && rmaLine.nonReturnableMessage.length > 0,
            )
        );
    }, [invoice, selectedInvoiceRmaLines]);

    const returnReasons = useMemo(() => {
        return invoice && "rmaReasonCode" in invoice.properties
            ? (JSON.parse(invoice.properties.rmaReasonCode) as ReturnReasonModel[])
            : [];
    }, [invoice]);

    if (!session || (!invoice && !isLoading)) {
        return null;
    }

    const practiceContactLabel = translate("Practice contact");

    const practiceContactChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        resetGeneralAndLineErrors();
        setPracticeContact(event.target.value);
        setPracticeContactError(!event.target.value.trim() ? getRequiredFieldMessage(practiceContactLabel) : "");
    };

    const updateInvoiceRmaLineChangeHandler = (
        invoiceRmaLine: InvoiceRmaLine,
        updatedProps: SafeDictionary<string | number | boolean>,
    ) => {
        resetGeneralAndLineErrors();
        deleteInvoiceRmaLineError(invoiceRmaLine.line.properties.rmaLineNumber);

        updateInvoiceRmaLine({
            invoiceNumber: invoice.invoiceNumber,
            invoiceRmaLine: {
                ...invoiceRmaLine,
                ...updatedProps,
            },
        });
    };

    const returnReasonChangeHandler = (event: FormEvent<HTMLSelectElement>, invoiceRmaLine: InvoiceRmaLine) => {
        updateInvoiceRmaLineChangeHandler(invoiceRmaLine, {
            rmaReasonCode: event.currentTarget.value,
            returnReasonFieldError:
                event.currentTarget.value === constants.rmaReasonCode.incorrectProductReceived &&
                !invoiceRmaLine.returnReasonComments,
        });
    };

    const returnAgreementChangeHandler = () => {
        setReturnAgreement(!returnAgreement);
    };

    const checkForErrors = () => {
        const practiceContactError =
            !practiceContact || !practiceContact.trim() ? getRequiredFieldMessage(practiceContactLabel) : "";
        setPracticeContactError(practiceContactError);
        const anyLineHasAnyError =
            selectedInvoiceRmaLines.filter(
                ([key, invoiceRmaLine]) =>
                    invoiceRmaLine?.hasAnyLocalLineFieldError === true ||
                    invoiceRmaLine?.returnReasonFieldError === true,
            ).length > 0 || Object.keys(invoiceRmaLineErrors || {}).length > 0;

        return !!(practiceContactError || generalError || anyLineHasAnyError);
    };

    const resetGeneralAndLineErrors = () => {
        if (generalError) {
            setGeneralError("");
        }
    };

    const deleteInvoiceRmaLineError = (lineNumber: string) => {
        if (invoiceRmaLineErrors && invoiceRmaLineErrors[lineNumber]) {
            delete invoiceRmaLineErrors[lineNumber];
        }
    };

    const generateReturnsSlipClickHandler = async () => {
        if (checkForErrors()) {
            return;
        }

        const isReCaptchaValid = await validateReCaptcha({
            location: "OrderStatus",
            recaptchaRef,
        });

        if (!isReCaptchaValid) {
            return;
        }

        setInProgress(true);
        submitReturnRequestForm({
            invoiceNumber: invoice.invoiceNumber,
            practiceContact,
            onSuccess: result => {
                setInProgress(false);
                toasterContext.addToast({
                    body: translate("Return Request generated successfully"),
                    messageType: "success",
                    timeoutLength: 1000 * 60 * 60 * 24 * 365,
                });

                setPracticeContact("");
                setReturnAgreement(false);
                setReloadInvoice(true);

                if (returnSlipPageUrl) {
                    setTimeout(() => {
                        window.open(`${returnSlipPageUrl}?returnSlipId=${result.returnSlipId}`, "_blank");
                    }, 1000);
                }
            },
            onError: error => {
                setInProgress(false);
                toasterContext.addToast({
                    body: translate("There was an error processing your request."),
                    messageType: "danger",
                });

                setGeneralError(error?.validationMessage || "");

                if (error?.lineErrors && error.lineErrors.length > 0) {
                    const invoiceRmaLineErrorsMap: SafeDictionary<string> = {};

                    for (const lineError of error.lineErrors) {
                        invoiceRmaLineErrorsMap[lineError.rmaLineNumber] = lineError.validationMessage;
                    }

                    setInvoiceRmaLineErrors(invoiceRmaLineErrorsMap);
                }
            },
        });
    };

    return (
        <Block {...styles.block} data-test-selector="returnRequest_formBlock">
            <LoadingOverlay loading={isLoading} {...styles.loadingOverlay}>
                <FormWrapper
                    onlyFocus
                    delayed
                    data-test-selector="returnRequest_formWrapper"
                    generalErrorMessage={generalError}
                >
                    <GridContainer {...styles.container}>
                        <GridItem {...styles.practiceContactGridItem}>
                            <TextField
                                label={practiceContactLabel}
                                value={practiceContact}
                                error={practiceContactError}
                                onChange={practiceContactChangeHandler}
                                data-test-selector="returnRequest_practiceContact"
                                required
                                maxLength={50}
                                {...styles.practiceContactTextField}
                            />
                        </GridItem>
                        <GridItem {...styles.invoiceLinesGridItem}>
                            <DataTable {...styles.dataTable}>
                                <DataTableHead {...styles.tableHead}>
                                    <DataTableHeader {...styles.tableHeader} className="description-column">
                                        {translate("Product")}
                                    </DataTableHeader>
                                    {isDesktop && (
                                        <>
                                            <DataTableHeader {...styles.tableHeader} className="quantity-column">
                                                {translate("Quantity")}
                                            </DataTableHeader>
                                            <DataTableHeader {...styles.tableHeader} className="fridgeref-column">
                                                {translate("Fridge ref")}
                                            </DataTableHeader>
                                        </>
                                    )}
                                    {isTablet && (
                                        <DataTableHeader {...styles.tableHeader} className="return-column">
                                            {translate("Return")}
                                        </DataTableHeader>
                                    )}
                                </DataTableHead>
                                <DataTableBody {...styles.tableBody}>
                                    {selectedInvoiceRmaLines &&
                                        selectedInvoiceRmaLines.length > 0 &&
                                        selectedInvoiceRmaLines.map(([key, invoiceRmaLine]) => {
                                            if (!invoiceRmaLine) {
                                                return null;
                                            }

                                            return (
                                                <ReturnRequestLineCard
                                                    key={key}
                                                    invoiceRmaLine={invoiceRmaLine}
                                                    returnReasons={returnReasons}
                                                    totalQuantity={totalQuantity}
                                                    invoiceRmaLineError={
                                                        invoiceRmaLineErrors &&
                                                        invoiceRmaLineErrors[
                                                            invoiceRmaLine.line.properties.rmaLineNumber
                                                        ] &&
                                                        invoiceRmaLineErrors[
                                                            invoiceRmaLine.line.properties.rmaLineNumber
                                                        ]
                                                    }
                                                    language={session.language || undefined}
                                                    currencySymbol={session.currency?.currencySymbol}
                                                    updateInvoiceRmaLineChangeHandler={
                                                        updateInvoiceRmaLineChangeHandler
                                                    }
                                                    returnReasonChangeHandler={returnReasonChangeHandler}
                                                />
                                            );
                                        })}
                                </DataTableBody>
                            </DataTable>
                        </GridItem>
                    </GridContainer>
                    <ReCaptcha recaptchaRef={recaptchaRef} location="OrderStatus" />
                    <StyledWrapper {...styles.formActionsWrapper}>
                        <Checkbox
                            {...styles.returnAgreement}
                            onChange={returnAgreementChangeHandler}
                            checked={returnAgreement}
                            uid="returnAgreement"
                            data-test-selector="returnRequest_returnAgreement"
                        >
                            {translate("mwi_return_agreement_label")}
                            <RequiredWrapper />
                        </Checkbox>
                        <Button
                            {...styles.returnRequestButton}
                            onClick={generateReturnsSlipClickHandler}
                            data-test-selector="returnRequest_generateReturnsSlipSubmit"
                            disabled={generateButtonDisabled || !returnAgreement || inProgress}
                        >
                            {translate("Generate Returns Slip")}
                        </Button>
                    </StyledWrapper>
                </FormWrapper>
            </LoadingOverlay>
        </Block>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ReturnRequestForm),
    definition: {
        group: constants.widgetGroup.returnRequest,
        allowedContexts: [constants.pageType.returnRequestRmaPage],
        displayName: "Return Request Form",
    },
};

export default widgetModule;
